import { useCallback, VFC } from "react";

import { Box } from "@portex-pro/ui-components";
import { ContractPricingFrequency } from "api/rest/rfp/brokerBidContractApi/types/ContractPricingFrequency";
import useEnableIntermodal from "hooks/useEnableIntermodal";
import useLDFlag from "hooks/useLDFlag";
import { useEffectOnce } from "usehooks-ts";

import { useCreateContractRequestMutation } from "../../api/bidRequestApi";
import { deliveryWeekField } from "../../const";
import useChangeBidRequestRoute from "../../hooks/useChangeBidRequestRoute";
import { useResetBidRequest, useUpdateBidRequest } from "../../store/bidRequestContractSlice";
import { useRemoveOneField, useUpdateOneField, useUpsertField } from "../../store/bidRequestFieldsSlice";
import { useBidRequestSliceSelector } from "../../store/bidRequestStore";
import { StaticFieldKeys } from "../../types";
import BidRequestPageSteps from "../../types/BidRequestPageSteps";
import CreateNewBidView from "./components/CreateNewBidView";
import SelectBidType from "./components/SelectBidType";

const CreateBidContainer: VFC = () => {
  const bidRequestState = useBidRequestSliceSelector((state) => state.bidRequestContractSlice);
  const updateBidRequest = useUpdateBidRequest();
  const upsertField = useUpsertField();
  const updateField = useUpdateOneField();
  const removeOneField = useRemoveOneField();
  const resetBidRequest = useResetBidRequest();
  const [createContractRequest] = useCreateContractRequestMutation();
  const changeBidRequestRoute = useChangeBidRequestRoute();
  const enableIntermodal = useEnableIntermodal();
  const enableLtlPartialMode = useLDFlag("enableLtlPartialMode");
  const enableRfpBulkSpotQuote = useLDFlag("enableRfpBulkSpotQuote");

  const handleCreateNewBid = useCallback(async () => {
    const res = await createContractRequest(bidRequestState).unwrap();

    changeBidRequestRoute({ step: BidRequestPageSteps.Trailer, rfpId: res.id });
  }, [bidRequestState, createContractRequest, changeBidRequestRoute]);

  const handlePricingFrequencyChange = useCallback(
    (value: ContractPricingFrequency) => {
      updateBidRequest({
        pricing_frequency: value,
        start: value === ContractPricingFrequency.BULK_SPOT_QUOTE ? undefined : bidRequestState.start,
        end: value === ContractPricingFrequency.BULK_SPOT_QUOTE ? undefined : bidRequestState.end,
        // Clear deadline when switching to Spot Bulk Quote
        deadline: value === ContractPricingFrequency.BULK_SPOT_QUOTE ? undefined : bidRequestState.deadline,
      });

      if (value === ContractPricingFrequency.WEEKLY) {
        upsertField(deliveryWeekField);
      } else {
        removeOneField(StaticFieldKeys.DELIVERY_WEEK);
      }

      if (value === ContractPricingFrequency.BULK_SPOT_QUOTE) {
        updateField({
          id: "stop_0_date",
          changes: { selected: true, required: false },
        });
        updateField({
          id: "stop_1_date",
          changes: { selected: true, required: false },
        });
      } else {
        updateField({ id: "stop_0_date", changes: { selected: false, required: false, validators: undefined } });
        updateField({ id: "stop_1_date", changes: { selected: false, required: false, validators: undefined } });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateBidRequest, bidRequestState.start, bidRequestState.end, bidRequestState.deadline]
  );

  useEffectOnce(() => {
    resetBidRequest();
  });

  return (
    <Box p={2}>
      <SelectBidType
        value={bidRequestState.mode}
        onChange={(value) => updateBidRequest({ mode: value })}
        disableIntermodal={!enableIntermodal}
        disableLtlPartial={!enableLtlPartialMode}
      />
      <CreateNewBidView
        bidDeadline={bidRequestState.deadline}
        bidTitle={bidRequestState.title ?? ""}
        mode={bidRequestState.mode}
        pricingFrequency={bidRequestState.pricing_frequency}
        contractStartDate={bidRequestState.start}
        contractEndDate={bidRequestState.end}
        enableRfpBulkSpotQuote={!!enableRfpBulkSpotQuote}
        onBidDeadlineChange={(value) => updateBidRequest({ deadline: value?.toISO() })}
        onBidTitleChange={(value) => updateBidRequest({ title: value })}
        onPricingFrequencyChange={handlePricingFrequencyChange}
        onContractStartDateChange={(value) => updateBidRequest({ start: value?.toISO() })}
        onContractEndDateChange={(value) => updateBidRequest({ end: value?.toISO() })}
        onSubmit={handleCreateNewBid}
        boxProps={{ width: "100%", py: "1rem" }}
      />
    </Box>
  );
};

export default CreateBidContainer;
